<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { UiDialog, UiText } from '@shopexperts/shared';
import type { AdminExpertClaim } from '@shopexperts/legacy-api';
import ClaimNoteField from './ClaimNoteField.vue';
import { isClaimNoteOverLimit } from './claimAppearance';

const props = defineProps<{
  claim: AdminExpertClaim | null;
  isLoading?: boolean;
  /** Unsaved drawer note draft, prefilled so typed context isn't lost. */
  initialNote?: string;
}>();

const model = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  confirm: [note?: string];
}>();

const note = ref('');

watch(model, (isOpen) => {
  note.value = isOpen ? (props.initialNote ?? '') : '';
});

const isNoteOverLimit = computed(() => isClaimNoteOverLimit(note.value));

function handleConfirm() {
  if (isNoteOverLimit.value) return;
  const trimmed = note.value.trim();
  emit('confirm', trimmed || undefined);
}
</script>

<template>
  <UiDialog
    v-model="model"
    title="Reject claim"
    :text="claim ? `Reject ${claim.claimant_name || claim.email}'s claim of ${claim.expert.name || claim.expert.slug}?` : ''"
    primary-action-label="Reject claim"
    primary-action-variant="danger"
    secondary-action-label="Cancel"
    :loading="isLoading"
    @primary-action="handleConfirm"
    @secondary-action="model = false"
  >
    <div class="flex flex-col gap-4">
      <UiText
        as="p"
        variant="p-small"
        class="text-gray-600"
        text="No email is sent to the claimant. You can reopen the claim later with Cancel decision."
      />

      <ClaimNoteField
        v-model="note"
        label="Admin note (optional)"
        placeholder="Add a note about this decision..."
        :disabled="isLoading"
      />
    </div>
  </UiDialog>
</template>
